import type { Container } from 'pixi.js';
import { SECTORS } from '@/config/sectors';
import {
  isEndlessUnlocked, isSectorUnlocked, setStartWeapon, startWeapon, startWeaponChoices,
  unlockedSectorCount,
} from '@/core/meta';
import { SAVE_SLOTS, clearSlot, loadSlot } from '@/core/save';
import { t } from '@/i18n';
import type { RunSetup } from '@/run/run-state';
import { sfx } from '@/audio/sfx';
import { rowPanel, slotRow } from './title-rows';
import { SECTOR_LAYOUT, buildLoadoutRow, buildSectorCard, sectorCardX } from './title-sectors';

export interface TitleCallbacks {
  /** Fresh run in `slot` (sector start or ENDLESS) */
  startRun(slot: number, setup: RunSetup): void;
  continueGame(slot: number): void;
  openSettings(): void;
  openCalibration(): void;
}

/** main = key art sharp; every submenu blurs it (docs/12 J1 + I5). */
export type Mode = 'main' | 'sectors' | 'new' | 'continue' | 'achievements';

export interface Item {
  build(selected: boolean, y: number): Container;
  run(): void;
  /** Slot index when ✕/Ⓧ should offer deletion */
  slot?: number;
  /** ←/→ on this item (the start-weapon row cycles) */
  side?(dir: number): void;
}

/** What the menus need from the title screen. */
export interface TitleHost {
  screenW(): number;
  mode(): Mode;
  setMode(mode: Mode, selected?: number): void;
  rebuild(): void;
  interactive(node: Container): Container;
}

/**
 * Title menu state + item lists (title-screen.ts owns layout and input):
 * the main menu, the sector select (cards + start weapon), the slot
 * pickers and the seed row.
 */
export class TitleMenus {
  seed: number | null = null;
  /** The run the slot picker will start (sector select or ENDLESS) */
  setup: Omit<RunSetup, 'seed'> = { mode: 'sector', sector: 0, weapon: startWeapon() };
  /** Sector card ↑ returns to from the start-weapon row */
  sectorFocus = 0;

  constructor(private readonly host: TitleHost, private readonly callbacks: TitleCallbacks) {}

  mainItems(): Item[] {
    const items: Item[] = [this.button(t('title.play'), () => this.openSectors())];
    if (this.anySave()) {
      items.push(this.button(t('title.continue'), () => this.host.setMode('continue')));
    }
    if (isEndlessUnlocked()) {
      items.push(this.button(t('title.endless'), () => {
        this.setup = { ...this.setup, mode: 'endless', sector: 0 };
        this.host.setMode('new');
      }));
    }
    items.push(this.button(t('title.achievements'), () => this.host.setMode('achievements')));
    items.push(this.button(t('title.settings'), () => this.callbacks.openSettings()));
    items.push(this.button(t('title.calibration'), () => this.callbacks.openCalibration()));
    return items;
  }

  /** Sector cards, the start-weapon row and BACK (design space). */
  sectorItems(): Item[] {
    const L = SECTOR_LAYOUT;
    const choices = startWeaponChoices();
    const items: Item[] = SECTORS.map((_, i) => ({
      build: (sel: boolean) => {
        const card = buildSectorCard(i, sel);
        card.position.set(sectorCardX(i), L.card.top);
        return this.host.interactive(card);
      },
      run: () => this.pickSector(i),
    }));
    items.push({
      build: (sel) => this.host.interactive(buildLoadoutRow(
        this.setup.weapon, Math.max(0, choices.indexOf(this.setup.weapon)), choices.length,
        sel, L.w / 2, L.loadoutY,
      )),
      run: () => this.cycleWeapon(1),
      side: (dir) => this.cycleWeapon(dir),
    });
    items.push(this.backItem(L.w / 2));
    return items;
  }

  pickerItems(): Item[] {
    const mode = this.host.mode() === 'continue' ? 'continue' : 'new';
    const items: Item[] = [];
    if (mode === 'new') items.push(this.seedItem());
    for (let i = 0; i < SAVE_SLOTS; i++) {
      const save = loadSlot(i);
      if (mode === 'continue' && save === null) continue;
      items.push({
        slot: save !== null ? i : undefined,
        build: (sel, y) => {
          const w = this.host.screenW();
          return this.host.interactive(slotRow(i, mode, sel, w / 2, y, w));
        },
        run: () => this.pickSlot(i),
      });
    }
    items.push(this.backItem());
    return items;
  }

  backItem(cx?: number): Item {
    return this.button(t('title.back'), () => this.back(), 300, 50, cx);
  }

  /** Header of the slot picker. */
  pickerTitle(): string {
    if (this.host.mode() === 'continue') return t('title.pickContinue');
    return this.setup.mode === 'sector'
      ? t('sector.pickSlot', { n: this.setup.sector + 1 })
      : t('title.pickEndless');
  }

  /** ◀ BACK / Esc / Ⓑ: a sector's slot picker returns to the sector select. */
  back(): void {
    if (this.host.mode() === 'new' && this.setup.mode === 'sector') {
      this.sectorFocus = this.setup.sector;
      this.host.setMode('sectors', this.setup.sector);
    } else {
      this.host.setMode('main');
    }
  }

  anySave(): boolean {
    for (let i = 0; i < SAVE_SLOTS; i++) if (loadSlot(i) !== null) return true;
    return false;
  }

  cycleWeapon(dir: number): void {
    const choices = startWeaponChoices();
    if (choices.length < 2) {
      sfx.uiDeny();
      return;
    }
    const at = Math.max(0, choices.indexOf(this.setup.weapon));
    const id = choices[(at + dir + choices.length) % choices.length]!;
    this.setup = { ...this.setup, weapon: id };
    setStartWeapon(id);
    sfx.uiMove();
    this.host.rebuild();
  }

  /** Sector select opens on the newest unlocked sector. */
  private openSectors(): void {
    this.setup = { ...this.setup, mode: 'sector', weapon: startWeapon() };
    this.sectorFocus = Math.max(0, unlockedSectorCount() - 1);
    this.host.setMode('sectors', this.sectorFocus);
  }

  private pickSector(i: number): void {
    if (!isSectorUnlocked(i)) {
      sfx.uiDeny();
      return;
    }
    this.setup = { ...this.setup, mode: 'sector', sector: i };
    this.host.setMode('new');
  }

  private pickSlot(i: number): void {
    if (this.host.mode() === 'continue') {
      if (loadSlot(i) !== null) this.callbacks.continueGame(i);
      return;
    }
    // The row already SAYS "OVERWRITE" — the player walked into the picker
    // on purpose, so overwrite goes through directly (Xbox virtual mouse)
    clearSlot(i);
    this.callbacks.startRun(i, { ...this.setup, seed: this.seed });
  }

  /** Seed row — Space/Ⓐ opens the system prompt; empty keeps it random. */
  private seedItem(): Item {
    return {
      build: (sel, y) => {
        const v = this.seed === null ? t('title.seedRandom') : String(this.seed);
        const cx = this.host.screenW() / 2;
        return this.host.interactive(rowPanel(t('title.seed', { v }), sel, cx, y, 420, 46, 15));
      },
      run: () => {
        const raw = window.prompt(t('title.seedPrompt'), this.seed?.toString() ?? '');
        if (raw === null) return;
        const text = raw.trim();
        if (text === '') this.seed = null;
        else if (/^\d+$/.test(text)) this.seed = Number(text) >>> 0;
        else {
          let h = 5381; // non-numeric seeds hash deterministically (djb2)
          for (let i = 0; i < text.length; i++) h = ((h * 33) ^ text.charCodeAt(i)) >>> 0;
          this.seed = h || 1;
        }
        this.host.rebuild();
      },
    };
  }

  private button(text: string, run: () => void, w = 360, h = 56, cx?: number): Item {
    return {
      build: (sel, y) => this.host.interactive(
        rowPanel(text, sel, cx ?? this.host.screenW() / 2, y, w, h, 22),
      ),
      run,
    };
  }
}
